import React, { useEffect, useState } from 'react';
import './FadeInImage.css';

/**
 * Reusable image that preloads and fades in once ready.
 * `as="div"` renders a background image; default renders an <img>.
 */
function FadeInImage({
	src,
	fallbackSrc,
	alt = '',
	className = '',
	as = 'img',
	style = {},
	backgroundSize = 'cover',
	backgroundPosition = 'center',
	backgroundRepeat = 'no-repeat',
	...rest
}) {
	const [loaded, setLoaded] = useState(false);
	const [displaySrc, setDisplaySrc] = useState(src);

	useEffect(() => {
		if (!src) return undefined;

		let isMounted = true;
		setLoaded(false);
		setDisplaySrc(src);
		const img = new Image();
		let usingFallback = false;
		img.loading = 'eager';
		if (img.decode) {
			img.decoding = 'async';
		}

		const markLoaded = () => {
			if (!isMounted) return;
			if (img.decode) {
				img
					.decode()
					.then(() => isMounted && setLoaded(true))
					.catch(() => isMounted && setLoaded(true));
				return;
			}
			setLoaded(true);
		};

		const handleError = () => {
			if (!isMounted) return;
			if (fallbackSrc && !usingFallback) {
				usingFallback = true;
				setDisplaySrc(fallbackSrc);
				img.src = fallbackSrc;
			} else {
				markLoaded();
			}
		};

		img.addEventListener('load', markLoaded);
		img.addEventListener('error', handleError);
		img.src = src;
		if (img.complete) {
			if (img.naturalWidth > 0) markLoaded();
			else handleError();
		}

		return () => {
			isMounted = false;
			img.removeEventListener('load', markLoaded);
			img.removeEventListener('error', handleError);
		};
	}, [src, fallbackSrc]);

	if (as === 'div') {
		const backgroundStyle = { backgroundImage: `url('${displaySrc}')` };
		return (
			<div
				className={`fade-image ${loaded ? 'isLoaded' : 'isLoading'} ${className}`}
				style={{
					backgroundSize,
					backgroundPosition,
					backgroundRepeat,
					visibility: loaded ? 'visible' : 'hidden',
					...style,
					...backgroundStyle,
				}}
				aria-hidden
				{...rest}
			/>
		);
	}

	return (
		<img
			src={displaySrc}
			alt={alt}
			className={`fade-image ${loaded ? 'isLoaded' : 'isLoading'} ${className}`}
			style={{
				visibility: loaded ? 'visible' : 'hidden',
				...style,
			}}
			loading='eager'
			{...rest}
		/>
	);
}

export default FadeInImage;
